"use client"

import type React from "react"

import { useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Share2, Bookmark, AlertCircle, X, Grid3X3, Check, ArrowRight, Plus, Phone, MessageSquare, Calendar, Flag } from "lucide-react"
import { useState, useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { ContactOwnerModal } from "@/components/contact-owner-modal"
import { SignUpPromptModal } from "@/components/signup-prompt-modal"
import { ReportDialog } from "@/components/moderation/report-dialog"
import { useSupabase } from "@/lib/supabase-client"
import { useToast } from "@/hooks/use-toast"
import { formatCity, formatShopAddress, formatState } from "@/lib/format-location"
import { shopNameStyle } from "@/lib/shop-name-size"
import { getAmenityById } from "@/lib/amenities"
// Social links only — NEVER booking links. These strip query strings, and
// booking links carry ?src= for channel attribution.
import { normalizeInstagramUrl, normalizeTikTokUrl, normalizeYouTubeUrl } from "@/lib/social-urls"
import { InstagramGrid, instagramHandle } from "@/components/listings/instagram-grid"
import { RevealOnScroll } from "@/components/reveal-on-scroll"
import { usePostHog } from "posthog-js/react"
import { trackMeta } from "@/lib/meta-pixel"
import { useUserRole } from "@/providers/user-role-provider"
import { readClientView } from "@/lib/client-view"
import { PulseDot } from "@/components/ui/pulse-dot"
import dynamic from "next/dynamic"
import { BarberChair } from "@/components/floor-plan/barber-chair"
import { BoothMark } from "@/components/ui/booth-mark"

// Concave corner for the guest pass ear on the plan cards: card colour in the
// top-left, transparent past the curve.
//
// The edge is centred on the 12px radius (11.75 → 12.25), not inside it. At
// 11.5 → 12 the drawn curve ended a quarter pixel short of the hero's straight
// edges, so where the curve met them there was a small step — visible as a
// ledge at the foot of each curve on a phone.
const EAR_FILLET = {
  width: 12,
  height: 12,
  background: "radial-gradient(circle at 100% 100%, transparent 11.75px, #0a0a0a 12.25px)",
} as const

// Same lazy 3D chair the owner's floor plan draws. Loaded on demand so the
// WebGL chunk stays out of every other page's bundle, with the drawn chair
// standing in until it arrives — a plan card is never empty.
const BoothChair3D = dynamic(
  () => import("@/components/floor-plan/booth-chair-3d").then((m) => m.BoothChair3D),
  { ssr: false, loading: () => null },
)

// Matches the floor plan's chair box so a booth is the same size object on
// both sides of the product.
const PLAN_CHAIR_W = 70
const PLAN_CHAIR_H = 89

interface BoothOption {
  id: string
  name?: string
  billing_period?: string
  plan_type?: string  // Support both column names for backwards compatibility
  price: number
  commission_rate?: number | null
  description: string
  is_enabled?: boolean
  sessions?: number
  /** Short-term rate for a visiting barber. Renders inverted — see the card below. */
  is_guest_pass?: boolean | null
}

const isCommissionPlan = (p: BoothOption) => (p.billing_period || p.plan_type) === "commission"

interface Listing {
  id: string
  name: string
  slug?: string
  description: string
  address: string
  city: string
  state: string
  zip: string
  shop_type: string
  total_barbers: number
  available_booths: number
  security_deposit: number
  cover_image: string
  logo_url: string
  google_rating: number
  google_reviews_count: number
  amenities: string[]
  booth_plans: BoothOption[]
  // No stripe_connect_id here on purpose — the page is public, and the acct_
  // id is not needed to decide whether the shop can take money.
  stripe_account_status?: string | null
  shop_images: Array<{ id: string; image_url: string }>
  owner_id: string
  hide_rates?: boolean
  booking_url?: string | null
  booking_platform?: string | null
  instagram_url?: string | null
  tiktok_url?: string | null
  youtube_url?: string | null
  contact_label?: string | null
  phone?: string | null
}

const SHOP_BOOKING_LABELS: Record<string, string> = {
  squire: "Squire",
  booksy: "Booksy",
  thecut: "The Cut",
  fresha: "Fresha",
  fullcap: "FullCap",
  other: "Book online",
}

const SHOP_BOOKING_LOGOS: Record<string, string> = {
  squire: "/booking-logos/squire.svg",
  booksy: "/booking-logos/booksy.svg",
  thecut: "/booking-logos/thecut.svg",
  fresha: "/booking-logos/fresha.svg",
  fullcap: "/booking-logos/fullcap.svg",
}

// Same fallback as the barber profile — sniff the URL when the saved
// platform is missing (legacy rows or 'other').
function detectShopBookingPlatform(url: string | null | undefined): string {
  const lower = (url || "").toLowerCase()
  if (lower.includes("getsquire") || lower.includes("squireapp") || lower.includes("squire.com")) return "squire"
  if (lower.includes("booksy")) return "booksy"
  if (lower.includes("thecut")) return "thecut"
  if (lower.includes("fresha")) return "fresha"
  if (lower.includes("fullcap")) return "fullcap"
  return ""
}

interface JobListingPreview {
  id: string
  slug: string | null
  title: string
  position_type: string[]
  compensation: string | null
  compensation_details: string | null
  created_at: string
}

// CLIENT booking lens — inner content for the questions/contact card when the
// viewer is a logged-in client. Renders a contact row (Call / Text / Instagram)
// above a "Book an appointment" CTA that drops into the shop booking funnel.
// No card wrapper: the desktop + mobile slots already provide one.
function ShopClientBooking({ shop, hasInAppBooking }: { shop: Listing; hasInAppBooking?: boolean }) {
  const phone = shop.phone?.trim()
  const tel = phone ? phone.replace(/[^\d+]/g, "") : ""
  // Some shops live on Discover but take bookings through an EXTERNAL platform
  // (Booksy / Squire / …). Surface that platform link instead of in-app booking;
  // only shops without a booking_url drop into the in-app appointment funnel.
  const platformKey = shop.booking_url
    ? (SHOP_BOOKING_LOGOS[shop.booking_platform || ""] ? (shop.booking_platform as string) : detectShopBookingPlatform(shop.booking_url))
    : null
  // Named platform (Booksy/Squire/…) → "Book on Booksy" + its logo; any other/
  // custom URL → "Book online" (never "Book on null").
  const platformName = platformKey && platformKey !== "other" ? (SHOP_BOOKING_LABELS[platformKey] || null) : null
  const platformLogo = platformKey ? SHOP_BOOKING_LOGOS[platformKey] : null
  // Compact, brand-led primary pill — a white logo chip (so colored platform
  // logos / the BOOTH mark always sit on white) + label. Auto width, not a bar.
  const bookBtn =
    "group inline-flex items-center gap-2 h-9 pl-1.5 pr-4 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
  const logoChip = "flex items-center justify-center w-6 h-6 rounded-full bg-white overflow-hidden flex-shrink-0"
  // Borderless contact links (no pill / no stroke) — plain icon + label. Gaps
  // are inline styles: Tailwind v4 spacing utilities can silently fail here,
  // leaving the icon + label cramped. (See feedback_exact_px_inline_style.)
  const contact = "text-sm font-medium text-foreground hover:text-muted-foreground transition-colors"
  const contactStyle = { display: "inline-flex", alignItems: "center", gap: "8px" } as const
  return (
    <div className="space-y-4">
      {(phone || shop.instagram_url) && (
        <div>
          <p className="text-sm font-medium text-foreground mb-3">Contact shop</p>
          <div className="flex flex-wrap" style={{ alignItems: "center", columnGap: "28px", rowGap: "12px" }}>
            {phone && (
              <a href={`tel:${tel}`} className={contact} style={contactStyle} aria-label="Call">
                <Phone className="w-3.5 h-3.5" strokeWidth={1.75} /> Call
              </a>
            )}
            {phone && (
              <a href={`sms:${tel}`} className={contact} style={contactStyle} aria-label="Text">
                <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.75} /> Text
              </a>
            )}
            {normalizeInstagramUrl(shop.instagram_url) && (
              <a href={normalizeInstagramUrl(shop.instagram_url)!} target="_blank" rel="noopener noreferrer" className={contact} style={contactStyle} aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
                </svg>
                Instagram
              </a>
            )}
          </div>
        </div>
      )}
      {shop.booking_url ? (
        <a href={shop.booking_url} target="_blank" rel="noopener noreferrer" className={bookBtn}>
          <span className={logoChip}>
            {platformLogo ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={platformLogo} alt={platformName || "Booking"} className="w-full h-full object-cover" />
            ) : (
              <Calendar className="w-3.5 h-3.5 text-black" />
            )}
          </span>
          {platformName ? `Book on ${platformName}` : "Book online"}
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
        </a>
      ) : hasInAppBooking ? (
        <Link href={`/book/shops/${shop.slug || shop.id}`} className={bookBtn}>
          <span className={logoChip}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/images/booth-icon-black.png" alt="BOOTH" className="w-4 h-4 object-contain" />
          </span>
          Book an appointment
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
        </Link>
      ) : null}
    </div>
  )
}

const ListingsPageClient = ({
  initialData,
  jobListings,
  params,
  fetchError,
  initialIsAuthed,
  hasInAppBooking = false,
}: { initialData: Listing | null; jobListings?: JobListingPreview[]; params: { id: string }; fetchError?: string | null; initialIsAuthed?: boolean; hasInAppBooking?: boolean }) => {
  const t = useTranslations("pages.listings")
  // CLIENT booking lens: a logged-in client views this shop to BOOK, not to
  // rent a chair or find a job. We hide Booth Options + Open Positions and swap
  // the "Contact owner" questions card for a "Book an appointment" card with
  // Call / Text / Instagram above it.
  const { isClient } = useUserRole()
  // Pros in Client mode (cookie) get the same booking lens as true clients.
  const [clientModeActive, setClientModeActive] = useState(false)
  useEffect(() => { setClientModeActive(readClientView()) }, [])
  const clientLens = isClient || clientModeActive
  // Localized amenity names keyed by amenity id; unknown ids fall back to
  // the lib/amenities label, then the raw id.
  const amenityLabels = t.raw("detail.amenities") as Record<string, string>

  // Shop-listing leaderboard signal. One event per listing view, tagged with
  // shop identity + a `shop` group so PostHog can rank listings by view count
  // (and unique viewers) without parsing slugs out of $pageview URLs. The ref
  // guard keeps React 18 StrictMode's double-mount from double-counting.
  const posthog = usePostHog()
  const viewCaptured = useRef(false)
  useEffect(() => {
    if (!posthog || !initialData?.id || viewCaptured.current) return
    viewCaptured.current = true
    posthog.capture("shop_listing_viewed", {
      shop_id: initialData.id,
      shop_name: initialData.name,
      shop_slug: initialData.slug ?? null,
      shop_city: initialData.city ?? null,
      shop_state: initialData.state ?? null,
      // Associate the event with a `shop` group so listings can be aggregated
      // group-wise in PostHog (the leaderboard also works off the props above).
      $groups: { shop: initialData.id },
    })
  }, [posthog, initialData?.id, initialData?.name, initialData?.slug, initialData?.city, initialData?.state])

  // Meta ViewContent, kept out of the effect above so a visitor without
  // PostHog loaded still counts. Lets the barber ads report how many people
  // went on to open a shop, and seeds a retargeting audience of shop viewers.
  const metaViewSent = useRef(false)
  useEffect(() => {
    if (!initialData?.id || metaViewSent.current) return
    metaViewSent.current = true
    trackMeta("ViewContent", {
      content_type: "shop_listing",
      content_ids: initialData.id,
      content_category: initialData.city ?? undefined,
    })
  }, [initialData?.id, initialData?.city])

  const [isFavorited, setIsFavorited] = useState(false)
  const [savedRecordId, setSavedRecordId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false)
  const [isSaveSignUpModalOpen, setIsSaveSignUpModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<BoothOption | null>(null)
  // Seed from the server-resolved auth flag so first paint already shows
  // the correct chrome (authed users no longer flash public→authed).
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(
    initialIsAuthed === undefined ? null : initialIsAuthed
  )
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [isReportOpen, setIsReportOpen] = useState(false)
  const [isOnboardedBarber, setIsOnboardedBarber] = useState(false)
  const [showAllPhotos, setShowAllPhotos] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const sliderRef = useRef(null)
  const router = useRouter()
  const supabase = useSupabase()
  const { toast } = useToast()

  // Shared shop identity for every funnel event off this page, so views,
  // saves, contacts and reserves all join on the same shop in PostHog.
  const trackShop = useCallback(
    (event: string, extra: Record<string, unknown> = {}) => {
      if (!posthog || !initialData?.id) return
      posthog.capture(event, {
        shop_id: initialData.id,
        shop_name: initialData.name,
        shop_slug: initialData.slug ?? null,
        shop_city: initialData.city ?? null,
        shop_state: initialData.state ?? null,
        $groups: { shop: initialData.id },
        ...extra,
      })
    },
    [posthog, initialData?.id, initialData?.name, initialData?.slug, initialData?.city, initialData?.state],
  )

  // Lock the page behind the photo lightbox so the wheel scrolls the gallery,
  // not the (now blurred, but still visible) page underneath.
  useEffect(() => {
    if (!showAllPhotos) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [showAllPhotos])

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setIsAuthenticated(!!user)
      setCurrentUserId(user?.id || null)

      if (user && initialData) {
        // Check if user is an onboarded barber at this shop
        const { data: barberData } = await supabase
          .from("shop_barbers")
          .select("status")
          .eq("barber_id", user.id)
          .eq("shop_id", initialData.id)
          .in("status", ["active", "approved"])
          .single()

        setIsOnboardedBarber(!!barberData)

        // Check if this shop is saved by the user
        const { data: savedData } = await supabase
          .from("saved_shops")
          .select("id")
          .eq("barber_id", user.id)
          .eq("shop_id", initialData.id)
          .maybeSingle()

        if (savedData) {
          setIsFavorited(true)
          setSavedRecordId(savedData.id)
        }
      }
    }
    checkAuth()
  }, [supabase, initialData])

  // Track listing view
  useEffect(() => {
    if (!initialData) return
    const trackView = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        // Don't track if the viewer is the shop owner
        if (user && user.id === initialData.owner_id) return
        await supabase.from("shop_listing_views").insert({
          shop_id: initialData.id,
          viewer_id: user?.id || null,
        })
      } catch {
        // Silently fail — view tracking is non-critical
      }
    }
    trackView()
  }, [supabase, initialData])

  // ---- every hook must sit ABOVE the early return below ----
  // These six used to be declared after `if (!initialData) return`, so a
  // render that showed the not-found card called 20 hooks and a render with
  // data called 26. React keys hooks by call order, so a parent that reused
  // this instance while flipping initialData from null to a shop (a soft
  // navigation landing on the same route position) threw "Rendered more hooks
  // than during the previous render". Caught by react-hooks/rules-of-hooks
  // the first time the linter was ever run on this repo.
  // One spin at a time, keyed by plan — hovering a second card should not
  // leave the first mid-rotation. Re-triggering needs the class removed for a
  // frame or the animation will not restart.
  const [spinningPlanId, setSpinningPlanId] = useState<string | null>(null)
  const [chairWebglFailed, setChairWebglFailed] = useState(false)
  const [flippedPlanId, setFlippedPlanId] = useState<string | null>(null)
  const spinPlanChair = useCallback((id: string) => {
    setSpinningPlanId(null)
    requestAnimationFrame(() => setSpinningPlanId(id))
  }, [])

  const handleToggleSave = useCallback(async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setIsSaveSignUpModalOpen(true)
      return
    }

    if (!currentUserId || !initialData) return

    setIsSaving(true)

    try {
      if (isFavorited && savedRecordId) {
        // Remove from saved
        const { error } = await supabase
          .from("saved_shops")
          .delete()
          .eq("id", savedRecordId)

        if (error) {
          console.error("Error removing saved shop:", error)
          toast({
            title: t("toasts.errorTitle"),
            description: t("detail.toasts.removeFailed"),
            variant: "destructive",
          })
        } else {
          setIsFavorited(false)
          setSavedRecordId(null)
          toast({
            title: t("toasts.removedTitle"),
            description: t("toasts.removedDesc"),
          })
        }
      } else {
        // Add to saved
        const { data, error } = await supabase
          .from("saved_shops")
          .insert({
            barber_id: currentUserId,
            shop_id: initialData.id,
          })
          .select("id")
          .single()

        if (error) {
          console.error("Error saving shop:", error)
          toast({
            title: t("toasts.errorTitle"),
            description: t("detail.toasts.saveFailed"),
            variant: "destructive",
          })
        } else {
          setIsFavorited(true)
          setSavedRecordId(data.id)
          trackShop("listing_saved")
          toast({
            title: t("toasts.savedTitle"),
            description: t("toasts.savedDesc"),
          })
        }
      }
    } catch (err) {
      console.error("Error toggling save:", err)
      toast({
        title: t("toasts.errorTitle"),
        description: t("toasts.updateFailed"),
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }, [isAuthenticated, currentUserId, initialData, isFavorited, savedRecordId, supabase, toast, t, trackShop])

  // Intent: clicked "Contact owner" (the marketplace lead funnel's middle step).
  const handleContactOwner = useCallback(() => {
    trackShop("contact_owner_clicked")
    setIsContactModalOpen(true)
  }, [trackShop])

  if (!initialData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">{t("detail.notFound.title")}</h1>
          <p className="text-muted-foreground mb-4">
            {t("detail.notFound.description")}
          </p>
          {fetchError && <p className="text-sm text-red-500 mb-4">{t("detail.notFound.errorPrefix")} {fetchError}</p>}
          <div className="flex flex-col gap-2">
            <Button onClick={() => router.push("/listings")} className="w-full">
              {t("detail.notFound.browseAll")}
            </Button>
            <Button variant="outline" onClick={() => router.back()} className="w-full">
              {t("detail.notFound.goBack")}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const shop = initialData

  // One deduped address string for the copy, the maps link and the embed, so
  // the three can never drift apart.

  const shopAddress = formatShopAddress(shop)
  const mapsEmbedKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_KEY

  const allImages = [{ id: "cover", image_url: shop.cover_image }, ...(shop.shop_images || [])].filter(
    (img) => img.image_url,
  )

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    setTouchEnd(e.changedTouches[0].clientX)
    handleSwipe()
  }

  const handleSwipe = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe && currentImageIndex < allImages.length - 1) {
      setCurrentImageIndex((prev) => prev + 1)
    }
    if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1)
    }
  }


  const handleReserveClick = async (plan: BoothOption) => {
    if (isAuthenticated === null) return

    // High-intent: clicked Reserve on a specific booth plan.
    trackShop("booth_reserve_clicked", {
      plan_id: plan.id,
      plan_name: plan.name ?? plan.plan_type ?? null,
      plan_price: plan.price ?? null,
    })

    if (isAuthenticated === false) {
      setSelectedPlan(plan)
      setIsSignUpModalOpen(true)
      return
    }

    // Navigate to the calendar booking flow with pre-selected plan
    router.push(`/listings/${shop.id}/book?plan=${plan.id}`)
  }


  const showPrices = !shop.hide_rates || isOnboardedBarber

  // A shop only takes money once its Connect account is active — same test
  // /api/shops/[id]/payment-status runs. Without this the Book Now CTA walked
  // a barber through picking days and choosing a session before the checkout
  // API refused them, which is a wall at the end of a corridor. Send them to
  // the owner instead, where the lead is still worth something.
  const acceptsPayments = shop.stripe_account_status === "active"

  // Auth-aware chrome — signed-in users sit under the floating wordmark and
  // bell, so the top offset clears them. Public viewers get marketing chrome
  // (tight top padding) so the heading sits right below PublicNavbar like
  // /listings does. Default to public chrome while auth resolves so the
  // more-common visitor case doesn't flash.
  const isAuthedForChrome = isAuthenticated === true
  // One horizontal anchor for both auth states, matching PageFrame's gutter.
  // Signed-in viewers used to get md:pl-28 / lg:pl-32 against a md:pr-6 right
  // edge — clearance for the top-left nav pill — which put the same shop page
  // in two different places depending on whether you were logged in.
  const chromeX = "md:px-6 lg:px-8"
  // Vertical still differs: signed-in viewers clear the floating wordmark and
  // bell, public viewers sit under PublicNavbar, which handles its own offset.
  const chromeY = isAuthedForChrome ? "md:pt-20" : "md:pt-0 lg:pt-0"

  // Public visitors get the EXACT same heading strip as /listings
  // (Browse Shops) — same paddings, same text classes, same alignment.
  // Authed users keep the existing app-chrome-aware heading because the
  // floating dock changes the horizontal anchor.
  const isPublicChrome = !isAuthedForChrome

  return (
    <div className="min-h-screen bg-background">
      {isPublicChrome ? (
        // Match the gallery + content stack's x-anchor exactly: outer
        // chromeX (lg:px-8 for public) + an inner lg:pl-7 on the h1 itself.
        // Without the inner pl-7 the heading was sitting ~32px to the left
        // of the gallery on lg, which read as broken alignment.
        // pt-4 assumed PublicNavbar injected its own 56px body padding-top
        // to clear its in-flow fixed bar. /listings moved to PublicNavGlass
        // (components/public-nav-glass.tsx), which floats via `fixed` and
        // reserves no layout space at all — so pt-4 alone left this page's
        // own sticky header (the gallery's back/share row, `sticky top-0`
        // below) rendering right underneath the floating pill on mobile.
        // pt-24 clears it the same way traction/pricing already do.
        <div className="pt-24 md:pt-16 lg:pt-16 pb-4 px-2.5 md:px-6 lg:px-8">
          {/* text-lg on mobile — the 2xl heading read oversized for a
              utility label like "View Shop". */}
          {/* -mt-3 on the phone lifts the label 12px closer to the floating
              nav (pt-24 above cleared it with room to spare). */}
          {/* h2, not h1: the shop name further down is the page's h1. */}
          <h2 className="-mt-3 md:mt-0 lg:pl-7 lg:pr-7 text-lg md:text-3xl lg:text-xl font-bold tracking-tight text-left text-foreground">
            {t("detail.title")}
          </h2>
        </div>
      ) : null}

      {/* px-2.5 on the phone, matching Browse Shops: the 16px gutter was
          costing the gallery and the plan cards width they use better than
          the margin does. sm+ is unchanged. */}
      <div className={`px-2.5 sm:px-6 ${chromeX} ${chromeY} md:pb-10 lg:pb-12 pb-[30svh] ${isPublicChrome ? "" : "pt-16"}`}>
        {/* Authed heading — sits outside space-y-8 so the gap to the gallery
            is tight (mb-3 = 12px) instead of inheriting the section rhythm.
            lg:pl-7 anchors to the same x as the gallery wrapper below. */}
        {!isPublicChrome && (
          <div className="lg:pl-7 mb-3">
            <h2 className="text-lg md:text-3xl lg:text-xl font-bold tracking-tight text-foreground">
              {t("detail.title")}
            </h2>
          </div>
        )}

        <div className="space-y-10">
          {/* Airbnb-style Gallery — lg:pl-7 + lg:pr-7 keeps the gutters
              symmetric so the gallery doesn't sit offset-left of center. */}
          <div className="lg:pl-7 lg:pr-7">
            {allImages.length > 0 && (
              <>
                {/* Mobile Gallery - Swipeable */}
                <div className="md:hidden">
                  <div
                    className="relative w-full rounded-[2.5rem] overflow-hidden bg-muted"
                    style={{ height: "55svh", minHeight: "350px" }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <Image
                      src={allImages[currentImageIndex]?.image_url || "/placeholder.svg"}
                      alt={`${shop.name} - Image ${currentImageIndex + 1}`}
                      fill
                      sizes="100vw"
                      className="object-cover"
                      priority
                    />

                    {currentImageIndex > 0 && (
                      <button
                        onClick={() => setCurrentImageIndex((prev) => prev - 1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-2 rounded-full shadow-sm"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    )}
                    {currentImageIndex < allImages.length - 1 && (
                      <button
                        onClick={() => setCurrentImageIndex((prev) => prev + 1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-2 rounded-full shadow-sm"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    )}

                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {currentImageIndex + 1} / {allImages.length}
                    </div>
                    {allImages.length > 1 && (
                      <button
                        onClick={() => {
                          setLightboxIndex(0)
                          setShowAllPhotos(true)
                        }}
                        className="absolute bottom-3 right-3 bg-white/80 backdrop-blur-sm text-black px-3 py-1 rounded-full text-xs font-medium hover:bg-white transition-colors"
                      >
                        {t("detail.gallery.viewAll")}
                      </button>
                    )}
                  </div>
                </div>

                {/* Desktop Gallery - Airbnb-style Grid */}
                <div className="hidden md:block">
                  <div
                    className="grid grid-cols-4 grid-rows-2 gap-2 rounded-[2.5rem] overflow-hidden cursor-pointer"
                    style={{ height: "60vh", minHeight: "400px", maxHeight: "500px" }}
                    onClick={() => {
                      setLightboxIndex(0)
                      setShowAllPhotos(true)
                    }}
                  >
                    {/* Main large image - spans 2 columns and 2 rows */}
                    <div className="col-span-2 row-span-2 relative bg-muted group">
                      <Image
                        src={allImages[0]?.image_url || "/placeholder.svg"}
                        alt={`${shop.name} - Main`}
                        fill
                        sizes="(max-width: 768px) 0px, 50vw"
                        className="object-cover group-hover:opacity-95 transition-opacity"
                        priority
                      />
                    </div>

                    {/* Top right image */}
                    <div className="relative bg-muted group">
                      <Image
                        src={allImages[1]?.image_url || allImages[0]?.image_url || "/placeholder.svg"}
                        alt={`${shop.name} - Image 2`}
                        fill
                        sizes="(max-width: 768px) 0px, 25vw"
                        className="object-cover group-hover:opacity-95 transition-opacity"
                      />
                    </div>

                    {/* Top far right image */}
                    <div className="relative bg-muted group">
                      <Image
                        src={allImages[2]?.image_url || allImages[0]?.image_url || "/placeholder.svg"}
                        alt={`${shop.name} - Image 3`}
                        fill
                        sizes="(max-width: 768px) 0px, 25vw"
                        className="object-cover group-hover:opacity-95 transition-opacity"
                      />
                    </div>

                    {/* Bottom right image */}
                    <div className="relative bg-muted group">
                      <Image
                        src={allImages[3]?.image_url || allImages[0]?.image_url || "/placeholder.svg"}
                        alt={`${shop.name} - Image 4`}
                        fill
                        sizes="(max-width: 768px) 0px, 25vw"
                        className="object-cover group-hover:opacity-95 transition-opacity"
                      />
                    </div>

                    {/* Bottom far right image with "Show all photos" button */}
                    <div className="relative bg-muted group">
                      <Image
                        src={allImages[4]?.image_url || allImages[0]?.image_url || "/placeholder.svg"}
                        alt={`${shop.name} - Image 5`}
                        fill
                        sizes="(max-width: 768px) 0px, 25vw"
                        className="object-cover group-hover:opacity-95 transition-opacity"
                      />
                      {/* Show all photos button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setLightboxIndex(0)
                          setShowAllPhotos(true)
                        }}
                        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-black/45 hover:bg-black/60 backdrop-blur-md text-white text-[11px] font-normal transition-colors"
                      >
                        <Grid3X3 className="w-3 h-3" strokeWidth={1.75} />
                        {t("detail.gallery.showAllPhotos")}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Full-screen photo gallery — z-[70] so it sits above the
                    nav launcher (z-50) and its popout (z-40).
                    Backdrop is a translucent blur (not solid black) so the
                    shop page stays faintly visible behind it. Photos stack
                    vertically and reveal as you scroll rather than swipe. */}
                {showAllPhotos && (
                  <div
                    className="fixed inset-0 z-[70] overflow-y-auto overscroll-contain"
                    onClick={() => setShowAllPhotos(false)}
                  >
                    {/* Frosted backdrop on its OWN fixed (non-scrolling) layer.
                        Keeping the blur off the scroll container stops the
                        white-flash repaint bug at the top edge when scrolling.
                        pointer-events-none so gutter clicks still close. */}
                    <div
                      aria-hidden
                      className="pointer-events-none fixed inset-0 bg-black/20 backdrop-blur-md"
                    />

                    {/* Sticky header. Controls get their own translucent-dark
                        pills so they stay legible against any photo or the
                        bright blurred page behind. */}
                    <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-4">
                      <button
                        onClick={() => setShowAllPhotos(false)}
                        className="flex items-center gap-2 rounded-full bg-black/50 px-3.5 py-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/70"
                      >
                        <X className="w-5 h-5" />
                        <span className="text-sm font-medium">{t("detail.gallery.close")}</span>
                      </button>
                      <div className="flex items-center gap-1.5 rounded-full bg-black/50 px-3.5 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                        <Grid3X3 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {allImages.length}
                      </div>
                    </div>

                    {/* Scrolling photo column. stopPropagation so clicks on a
                        photo don't close — only clicks on the side gutters do. */}
                    <div
                      className="relative z-[1] mx-auto max-w-5xl space-y-4 px-4 pb-20 pt-2 sm:space-y-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {allImages.map((img, idx) => (
                        <RevealOnScroll key={img.id}>
                          <div className="relative h-[78vh] w-full">
                            <Image
                              src={img.image_url || "/placeholder.svg"}
                              alt={`${shop.name} - Photo ${idx + 1}`}
                              fill
                              className="rounded-xl object-contain"
                              sizes="(max-width: 1024px) 100vw, 1024px"
                              priority={idx === 0}
                            />
                          </div>
                        </RevealOnScroll>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-6 lg:pl-7 lg:pr-7">
            <div className="flex items-start gap-4">
              {/* White (not black) matte: most shop logos are dark artwork on a
                  transparent background, so a black chip renders them as an
                  invisible black-on-black circle. New uploads bake their own
                  opaque matte (processLogoForUpload); white keeps legacy
                  transparent logos legible without a re-upload. */}
              {shop.logo_url && (
                <div className="rounded-full bg-white flex-shrink-0 overflow-hidden w-12 h-12">
                  <Image
                    src={shop.logo_url || "/placeholder.svg"}
                    alt={shop.name}
                    width={64}
                    height={64}
                    className="h-full object-cover w-full"
                  />
                </div>
              )}
              <div className="flex-1">
                {/* Size comes from shopNameStyle, not a text-* class: a long
                    name steps down 15% so it stops colliding with the
                    share/save buttons to its right. See lib/shop-name-size. */}
                <h1 className="font-bold leading-tight text-foreground" style={shopNameStyle(shop.name, 1.25)}>
                  {shop.name}
                </h1>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {formatCity(shop.city)}, {formatState(shop.state)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Shop social links — render only when populated. Black
                    minimal sharp icons with thin strokes per design. */}
                {(normalizeInstagramUrl(shop.instagram_url) || normalizeTikTokUrl(shop.tiktok_url) || normalizeYouTubeUrl(shop.youtube_url)) && (
                  <div className="flex items-center gap-0.5 sm:gap-1 sm:pr-1">
                    {normalizeInstagramUrl(shop.instagram_url) && (
                      <a
                        href={normalizeInstagramUrl(shop.instagram_url)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${shop.name} on Instagram`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-foreground hover:bg-foreground/5 transition-colors"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <rect x="3" y="3" width="18" height="18" rx="5" />
                          <circle cx="12" cy="12" r="4" />
                          <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
                        </svg>
                      </a>
                    )}
                    {normalizeTikTokUrl(shop.tiktok_url) && (
                      <a
                        href={normalizeTikTokUrl(shop.tiktok_url)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${shop.name} on TikTok`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-foreground hover:bg-foreground/5 transition-colors"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <path d="M14 4v9.5a3.5 3.5 0 1 1-3.5-3.5" />
                          <path d="M14 4c.5 2.5 2.5 4 5 4" />
                        </svg>
                      </a>
                    )}
                    {normalizeYouTubeUrl(shop.youtube_url) && (
                      <a
                        href={normalizeYouTubeUrl(shop.youtube_url)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${shop.name} on YouTube`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-foreground hover:bg-foreground/5 transition-colors"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <rect x="2.5" y="6" width="19" height="12" rx="3" />
                          <path d="M10.5 9.5v5l4-2.5z" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
                {/* Share/Save segmented pill — mirrors the listings density
                    toggle UI (bg-muted outer, p-0.5 rounded-lg; each button
                    p-1.5 rounded-md, active state lifts via bg-background +
                    shadow-sm). Save's active state is "bookmarked." */}
                <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted">
                  <button
                    type="button"
                    aria-label={t("detail.header.shareAria")}
                    onClick={() => {
                      navigator
                        .share({
                          title: shop.name,
                          text: t("detail.header.shareText", { name: shop.name }),
                          url: window.location.href,
                        })
                        .catch(() => {
                          navigator.clipboard.writeText(window.location.href)
                        })
                    }}
                    className="p-1.5 rounded-md text-foreground hover:bg-background/60 transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    aria-label={isFavorited ? t("detail.header.unsaveAria") : t("detail.header.saveAria")}
                    aria-pressed={isFavorited}
                    onClick={handleToggleSave}
                    disabled={isSaving}
                    className={`p-1.5 rounded-md transition-colors ${
                      isFavorited
                        ? "bg-background shadow-sm text-foreground"
                        : "text-foreground hover:bg-background/60"
                    }`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${isFavorited ? "fill-current" : ""}`} strokeWidth={1.75} />
                  </button>
                  {/* Guideline 1.2: a listing is someone else's photos and
                      copy on a public page, so it has to be reportable from
                      the page. Hidden on your own listing — the RLS policy
                      refuses a report against yourself anyway. */}
                  {currentUserId !== shop.owner_id && (
                    <button
                      type="button"
                      aria-label={t("detail.header.reportAria")}
                      onClick={() => setIsReportOpen(true)}
                      className="p-1.5 rounded-md text-foreground hover:bg-background/60 transition-colors"
                    >
                      <Flag className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable stacked sections — tighter rhythm matches the
                rest of the refreshed design system (max-w-2xl-ish content
                density, text-sm section headings, transparent surfaces). */}
            <div className="space-y-8">
              {/* 1. Overview + Booth Options on the left, Questions card on the
                  right. One row, so the sticky card rides alongside both and
                  stops where the booth cards stop — it used to be boxed into
                  the Overview row, which is often one line. */}
              <div className="flex flex-col md:flex-row md:gap-10 md:justify-between pb-8 border-b border-gray-200 dark:border-border">
                <div className="flex-1 min-w-0 space-y-8">
                  {/* Overview — max-w-3xl keeps the description readable
                      instead of stretching across the viewport. */}
                  <div className="md:max-w-3xl">
                    <h2 className="text-sm font-medium mb-3 text-foreground">{t("detail.overview")}</h2>
                    {/* whitespace-pre-line: an owner's blank lines and line
                        breaks are how they structured the description — headings,
                        bullet lists, paragraphs. Without it every description
                        collapsed into one unreadable blob no matter how it was
                        typed, so the formatting owners could enter was
                        formatting nobody could see. */}
                    <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{shop.description}</div>
                  </div>

                  {/* Booth Options + Open Positions are the barber-marketplace half of
                      the page (rent a chair / find a job) — hidden for client viewers,
                      who are here to book, not to work. */}
                  {!clientLens && (
              <div className={jobListings && jobListings.length > 0
                ? ((shop.booth_plans?.length ?? 0) >= 3
                    // 3+ plans need the whole width, so Open Positions stacks
                    // underneath instead of taking half the row.
                    ? "space-y-10"
                    : "space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-start")
                : ""}>
                {/* Booth Options */}
                <div className="pb-6 border-b border-gray-200 dark:border-border lg:border-b-0 lg:pb-0">
                  {/* No width cap. It used to apply to every count except exactly 4,
                      so one plan rendered at half the width of the identical
                      card on a four-plan shop. The grid already sizes the
                      cards; the section just needs the column. */}
                  <div className="space-y-3">
                    <h2 className="text-sm font-semibold text-foreground">{t("detail.plans.title")}</h2>


                    {/* Security Deposit — naked text block on the page bg.
                        Info icon + white card removed per design; the
                        deposit $ matches the booth-plan price green
                        (booth-green) so the two prices visually rhyme. */}
                    {shop.security_deposit > 0 && (
                      // pb-3 on top of the section's space-y-3: the deposit
                      // note and the first plan's label were close enough to
                      // read as one block.
                      <div className="pb-3">
                        <p className="text-sm font-medium text-foreground">
                          {t("detail.plans.securityDeposit")} <span className="text-booth-green font-semibold">${shop.security_deposit.toLocaleString()}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("detail.plans.depositNote")}
                        </p>
                      </div>
                    )}

                    {/* Booth Plans Grid — ONE layout for every shop, whatever
                        it lists: 2-up on mobile, three to a row from lg,
                        filling left to right. A lone plan is simply the first
                        cell of that grid, so it is the same width as a card on
                        a four-plan shop. It used to skip the grid for a wide
                        max-w-lg block, which left its right-aligned price and
                        CTA floating in the middle of an empty row. */}
                    {shop.booth_plans && shop.booth_plans.length > 0 ? (
                      // Wrapping row, not a grid: the cards are narrower than a third of
                      // the column, and grid cells kept the leftover as dead space between
                      // them. Now the gap is the gap.
                      <div className="flex flex-wrap gap-x-3 gap-y-5 lg:gap-x-4 lg:gap-y-6">
                        {[...shop.booth_plans]
                          // Commission has no price to sort by; it goes after the priced plans.
                          .sort((a, b) => (isCommissionPlan(a) ? 1 : 0) - (isCommissionPlan(b) ? 1 : 0) || a.price - b.price)
                          .map((plan) => {
                          // Support both billing_period and plan_type column names
                          const period = plan.billing_period || plan.plan_type || "monthly"
                          const periodLabel =
                            {
                              daily: t("detail.plans.perDay"),
                              weekly: t("detail.plans.perWeek"),
                              biweekly: t("detail.plans.perBiweekly"),
                              monthly: t("detail.plans.perMonth"),
                              commission: t("detail.plans.perCommission"),
                            }[period] || t("detail.plans.perMonth")
                          const planNameFallback =
                            {
                              daily: t("detail.plans.names.daily"),
                              weekly: t("detail.plans.names.weekly"),
                              biweekly: t("detail.plans.names.biweekly"),
                              monthly: t("detail.plans.names.monthly"),
                              commission: t("detail.plans.names.commission"),
                            }[period] || t("detail.plans.names.monthly")

                          // Commission is contact-only: BOOTH takes no cut of service
                          // revenue, so there is nothing to reserve or pay here.
                          const isCommission = period === "commission"
                          // No price set means the owner wants to be asked, not
                          // booked. Without this the card renders "$0.00" and a
                          // Book now that takes a zero-dollar reservation —
                          // which is the $0 listing hazard, one plan deeper than
                          // shops.hide_rates reaches.
                          const hasPrice = Number(plan.price) > 0
                          const canReserve = showPrices && acceptsPayments && !isCommission && hasPrice
                          const isFlipped = flippedPlanId === plan.id

                          // GUEST PASS — the inverted card. A guest pass is a
                          // short rate for a barber passing through, and it is
                          // the one option on the page aimed at someone who is
                          // NOT joining the floor. Same card, same content,
                          // same mechanics; the surface flips to black so it
                          // reads as a different kind of offer at a glance
                          // rather than a fourth rate to compare.
                          const isGuestPass = !!plan.is_guest_pass
                          const face = isGuestPass
                            ? "bg-neutral-950 text-white shadow-[0_1px_2px_rgba(0,0,0,0.22)]"
                            : "bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                          // On black, muted-foreground is a token tuned for the
                          // light ground and goes nearly invisible. These are
                          // the inverted equivalents, not new colours.
                          const titleTone = isGuestPass ? "text-white" : "text-foreground"
                          const mutedTone = isGuestPass ? "text-white/55" : "text-muted-foreground"
                          const actionTone = isGuestPass ? "text-white" : "text-foreground"
                          // Guest pass hero is white in light mode so the black
                          // chair stands out against the black card. Dark mode
                          // keeps the faint lift: html.dark .floor-card-hero in
                          // globals.css is unlayered and wins over this utility.
                          const heroTone = isGuestPass ? "bg-white text-white" : "bg-muted/50 text-foreground"
                          return (
                            <div
                              key={plan.id}
                              className="relative"
                              // Width in px (two-up on phones via the 44% cap), height inline —
                              // neither via a CSS class. The class lived in
                              // globals.css and Turbopack served a cached stylesheet
                              // without it, so the frame got NO height — and because both
                              // faces are absolutely positioned, the whole card collapsed
                              // to a sliver. Inline cannot be cached away, which is the
                              // same reason the project puts exact pixel sizes here.
                              style={{ perspective: 1200, height: 272, width: "min(236px, 44%)" }}
                            >
                              {/* Flip card, same mechanics as the floor plan:
                                  preserve-3d on the rotating wrapper, two
                                  absolutely-positioned faces with backface
                                  hidden. Front is the offer — chair, plan,
                                  price, Reserve. Back is the detail.

                                  Flipped by an explicit control, NOT by hover:
                                  Reserve lives on the front, and a card that
                                  turns when the cursor arrives would swing the
                                  button away from the pointer reaching for it. */}
                              <div
                                className="relative w-full h-full transition-transform duration-500 ease-out"
                                style={{
                                  transformStyle: "preserve-3d",
                                  transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                                }}
                              >
                                {/* FRONT */}
                                <div
                                  // Same FACE string the floor plan uses.
                                  // .floor-card carries the radius and the dark
                                  // glossy-glass surface from globals.css;
                                  // bg-white is the light one. Without it these
                                  // faces were bare divs on the page ground and
                                  // shared nothing with the floor plan but the
                                  // chair.
                                  className={`floor-card absolute inset-0 overflow-hidden ${face} p-2 pb-2.5 text-left flex flex-col gap-2`}
                                  style={{
                                    backfaceVisibility: "hidden",
                                    WebkitBackfaceVisibility: "hidden",
                                  }}
                                  aria-hidden={isFlipped}
                                >
                                  {/* Guest pass ear — a tab of the card's own
                                      surface cut into the hero's top-left
                                      corner, so the label reads as part of the
                                      card rather than a sticker on it. The two
                                      fillets curve the tab's feet into the
                                      hero's top and left edges (the hero sits
                                      8px in, the face's p-2).

                                      The tab's own corner is 10px, not more:
                                      the right fillet runs 8→20px down the
                                      tab's side, so that side has to stay
                                      straight to 20px (30 − 10). A rounder
                                      corner curved away under the fillet and
                                      left a hairline of the white hero
                                      showing. Each fillet also tucks 1px back
                                      under the tab, so a fractional tab width
                                      can't open an anti-aliased seam. */}
                                  {isGuestPass && (
                                    <div
                                      className="absolute left-0 top-0 z-10 inline-flex items-center gap-1 rounded-br-[10px] bg-neutral-950 pl-4 pr-2.5 pt-1.5 text-[9px] font-semibold uppercase tracking-wider text-white/90"
                                      // z-10 promotes the tab to its own layer, and
                                      // iOS Safari doesn't carry the face's
                                      // backface-visibility onto it — the tab
                                      // showed through mirrored on the flipped
                                      // card. Hide its own backface, and fade it
                                      // out on flip as a belt-and-braces.
                                      style={{
                                        height: 30,
                                        backfaceVisibility: "hidden",
                                        WebkitBackfaceVisibility: "hidden",
                                        opacity: isFlipped ? 0 : 1,
                                        transition: "opacity 150ms ease-out",
                                      }}
                                      aria-hidden={isFlipped}
                                    >
                                      {/* Lifted 1px: on the text's centre line the rings'
                                          bottom edge was clipped. Overflow visible so
                                          their anti-aliased edge isn't cut by the box. */}
                                      <BoothMark size={13} className="relative -top-px shrink-0 overflow-visible" />
                                      {t("detail.plans.guestPass")}
                                      <span aria-hidden className="absolute" style={{ ...EAR_FILLET, left: "calc(100% - 1px)", top: 8, width: 13 }} />
                                      <span aria-hidden className="absolute" style={{ ...EAR_FILLET, top: "calc(100% - 1px)", left: 8, height: 13 }} />
                                    </div>
                                  )}
                                  {/* The chair — same object and rounded panel
                                      the owner's floor plan uses, so a booth
                                      reads as one thing across the product. */}
                                  <div
                                    className={`floor-card-hero floor-card-hero-box chair-spin-stage w-full flex items-center justify-center ${heroTone}`}
                                    // Same reasoning as the frame height above.
                                    style={{ height: 140 }}
                                    onMouseEnter={() => spinPlanChair(plan.id)}
                                  >
                                    <div
                                      className={spinningPlanId === plan.id ? "chair-spin" : undefined}
                                      onAnimationEnd={() => setSpinningPlanId(null)}
                                      style={{ width: PLAN_CHAIR_W, height: PLAN_CHAIR_H }}
                                    >
                                      {chairWebglFailed ? (
                                        <BarberChair pct={null} />
                                      ) : (
                                        <BoothChair3D pct={null} onUnavailable={() => setChairWebglFailed(true)} />
                                      )}
                                    </div>
                                  </div>

                                  <div className="min-w-0 flex-1 px-0.5 flex flex-col">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <PulseDot />
                                      <p className={`text-[13px] font-medium capitalize truncate ${titleTone}`}>
                                        {plan.name || planNameFallback}
                                      </p>
                                    </div>
                                    {plan.description && (
                                      <p className={`text-[11px] leading-snug mt-1 line-clamp-2 ${mutedTone}`}>
                                        {plan.description}
                                      </p>
                                    )}

                                    {/* Details on its own line; the price and the action
                                        share the card's floor below. */}
                                    {/* 3px, inline: arbitrary Tailwind values don't build here. */}
                                    <div className="min-w-0" style={{ marginTop: 3 }}>
                                      {plan.description ? (
                                        <button
                                          onClick={() => setFlippedPlanId(plan.id)}
                                          className={`shrink-0 text-[11px] font-medium underline underline-offset-2 transition-colors ${isGuestPass ? "text-white/60 hover:text-white" : "text-muted-foreground hover:text-foreground"}`}
                                        >
                                          details
                                        </button>
                                      ) : (
                                        <span className="shrink-0" aria-hidden />
                                      )}
                                    </div>
                                  </div>

                                  {/* Floor of the card: price bottom-left, reserve bottom-right —
                                      the two things a barber weighs, on one baseline. Reserve is
                                      small, lowercase, with the plus tucked in; it's a text action,
                                      not a button. No hairline: the card's edge already separates
                                      it from the page. */}
                                  <div className="flex items-baseline justify-between gap-2 pl-0.5 mt-auto pt-2.5 min-w-0">
                                    {isCommission ? (
                                      // Only the rate, and only when the owner set one — the card
                                      // name already says commission and the button says contact.
                                      showPrices && plan.commission_rate ? (
                                        <div className="flex items-baseline gap-1 shrink-0">
                                          <span className="text-[15px] font-normal tracking-tight text-booth-green">
                                            {plan.commission_rate}%
                                          </span>
                                          <span className={`text-[10px] ${mutedTone}`}>{periodLabel}</span>
                                        </div>
                                      ) : (
                                        <span aria-hidden />
                                      )
                                    ) : showPrices && hasPrice ? (
                                      <div className="flex items-baseline gap-1 shrink-0">
                                        <span className="text-[15px] font-normal tracking-tight text-booth-green">
                                          ${plan.price.toFixed(2)}
                                        </span>
                                        <span className={`text-[10px] ${mutedTone}`}>{periodLabel}</span>
                                      </div>
                                    ) : showPrices ? (
                                      // Priced on request. The period still matters —
                                      // "ask, for a week" is a different offer from
                                      // "ask, for a month".
                                      <div className="flex items-baseline gap-1 shrink-0">
                                        <span className={`text-[13px] font-normal tracking-tight ${isGuestPass ? "text-white/70" : "text-muted-foreground"}`}>
                                          {t("detail.plans.priceOnRequest")}
                                        </span>
                                        <span className={`text-[10px] ${mutedTone}`}>{periodLabel}</span>
                                      </div>
                                    ) : (
                                      <span aria-hidden />
                                    )}
                                    {canReserve ? (
                                      <button
                                        onClick={() => handleReserveClick(plan)}
                                        disabled={isAuthenticated === null}
                                        className={`group inline-flex shrink-0 whitespace-nowrap items-center gap-0.5 text-[13px] sm:text-[11px] font-normal lowercase tracking-tight hover:opacity-70 active:opacity-50 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${actionTone}`}
                                      >
                                        <span>{t("detail.plans.bookNow")}</span>
                                        <Plus className="w-3.5 h-3.5 sm:w-3 sm:h-3 group-hover:rotate-90 transition-transform duration-200" strokeWidth={2} />
                                      </button>
                                    ) : (
                                      // The plus alone. "inquire +" shared one
                                      // baseline with the price, and on a
                                      // narrow card the word pushed the plus
                                      // past the edge — $900.00 /month left it
                                      // reading "inquire -|". The label is kept
                                      // for screen readers, where there is no
                                      // width to run out of.
                                      <button
                                        onClick={handleContactOwner}
                                        aria-label={t("detail.plans.contactShop")}
                                        className={`group inline-flex shrink-0 items-center justify-center -mr-1 h-7 w-7 rounded-full hover:opacity-70 active:opacity-50 transition-opacity duration-200 ${actionTone}`}
                                      >
                                        <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" strokeWidth={2} />
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* BACK — the way out first, then the detail.
                                    Back sat at the foot of the card, which put
                                    the exit at the end of a scroll: on a long
                                    description you had to read to the bottom to
                                    find your way home. It leads now.

                                    The whole face is also the control — tapping
                                    anywhere returns. The button stays because a
                                    card that only responds to a click nobody
                                    was told about is a card people get stuck
                                    on. */}
                                <div
                                  // pt-4 pl-4 rather than the front's p-2: the
                                  // back is a block of reading, and text set
                                  // hard against a card edge is harder to read
                                  // than the same text with a margin.
                                  className={`floor-card absolute inset-0 overflow-hidden ${face} p-2 pt-4 pl-4 pb-3 text-left flex flex-col gap-2.5 cursor-pointer`}
                                  style={{
                                    backfaceVisibility: "hidden",
                                    WebkitBackfaceVisibility: "hidden",
                                    transform: "rotateY(180deg)",
                                  }}
                                  aria-hidden={!isFlipped}
                                  onClick={() => setFlippedPlanId(null)}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setFlippedPlanId(null)
                                    }}
                                    className="self-start inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors px-0.5"
                                  >
                                    <ChevronLeft className="w-3 h-3" strokeWidth={2} />
                                    Back
                                  </button>

                                  <div className="px-0.5">
                                    <p className="text-[13px] font-medium text-foreground capitalize">
                                      {plan.name || planNameFallback}
                                    </p>
                                    {/* Moved off the front: on a card whose job
                                        is name + price + act, a third line of
                                        terms was competing with the price. It
                                        is detail, so it lives with the detail. */}
                                    {(plan.sessions && plan.sessions > 1) ? (
                                      // Green, like the price. Sessions are the
                                      // other half of what you get for the
                                      // money — in muted grey it read as a
                                      // footnote rather than part of the offer.
                                      <p className="text-[11px] font-medium text-booth-green mt-0.5">
                                        {t("detail.plans.sessionsIncluded", { count: plan.sessions })}
                                      </p>
                                    ) : null}
                                  </div>

                                  <p className="text-[11px] leading-relaxed text-muted-foreground whitespace-pre-line flex-1 overflow-y-auto px-0.5 pr-1.5 [scrollbar-width:thin] [scrollbar-color:theme(colors.zinc.400)_transparent]">
                                    {plan.description}
                                  </p>

                                  {/* Reserve lives on both faces, same corner,
                                      same hairline. Reading the detail is how
                                      someone decides to book — sending them
                                      back to the front to do it would put a
                                      flip between the decision and the action. */}
                                  <div
                                    className="flex items-center justify-start mt-auto pt-2.5 pl-0.5"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {canReserve ? (
                                      <button
                                        onClick={() => handleReserveClick(plan)}
                                        disabled={isAuthenticated === null}
                                        className="group inline-flex items-center gap-1.5 text-[13px] font-normal tracking-tight text-foreground hover:opacity-70 active:opacity-50 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <span>{t("detail.plans.bookNow")}</span>
                                        <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" strokeWidth={2} />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={handleContactOwner}
                                        className="group inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 text-[13px] font-normal tracking-tight text-foreground hover:opacity-70 active:opacity-50 transition-opacity duration-200"
                                      >
                                        <span>{t("detail.plans.contactShop")}</span>
                                        <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" strokeWidth={2} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                        {t("detail.plans.noPricing")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Open Positions — naked, no card surface. At lg a soft
                    left border + pl-8 creates the vertical divider between
                    Booth Options and Open Positions columns. */}
                {jobListings && jobListings.length > 0 && (
                  <div className="pb-6 border-b border-gray-200 dark:border-border lg:border-b-0 lg:pb-0 lg:border-l lg:border-gray-200 dark:lg:border-border lg:pl-8">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-foreground">{t("detail.positions.title")}</h2>
                        <span className="text-xs text-muted-foreground">
                          {t("detail.positions.open", { count: jobListings.length })}
                        </span>
                      </div>
                      {/* pt-2 on top of the section's space-y-3. Both columns
                          put 12px under their heading, but a booth plan is a
                          card: its own top padding pushes the first thing you
                          read down, while a job title starts at the very top
                          edge of its row. Equal margins therefore looked
                          unequal, with Open Positions riding high against the
                          cards beside it. Padding, not margin — space-y-3
                          already owns margin-top on this element. */}
                      <div className="space-y-3 pt-2">
                        {jobListings.map((job) => {
                          return (
                            <article
                              key={job.id}
                              className="flex flex-col gap-2"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <h3 className="font-medium text-sm leading-snug min-w-0 text-foreground">{job.title}</h3>
                                <a
                                  href={`/hiring/${job.slug || job.id}?from=shop`}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline underline-offset-4 flex-shrink-0"
                                >
                                  {/* Icon AFTER the label, matching Reserve,
                                      Contact Shop and every other CTA in the
                                      app. This one led with the plus, so two
                                      controls a few pixels apart put the same
                                      glyph on opposite sides. */}
                                  {t("detail.positions.viewRole")}
                                  <Plus className="w-3 h-3" strokeWidth={2} />
                                </a>
                              </div>

                              <div className="flex flex-wrap gap-1">
                                {job.position_type?.map((p) => (
                                  <span
                                    key={p}
                                    className="text-[10px] font-light bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full capitalize"
                                  >
                                    {p.replace("_", " ")}
                                  </span>
                                ))}
                                {job.compensation && (
                                  <span className="text-[10px] font-light bg-sky-50 text-sky-700 border border-sky-100 px-2 py-0.5 rounded-full capitalize">
                                    {job.compensation.replace("_", " ")}
                                  </span>
                                )}
                              </div>
                            </article>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
                  )}
                </div>
                  {/* Questions card - Right side. Snug width — sized
                      to the content (one-line CTA, wrapping copy) so
                      the right edge isn't dead white space. */}
                  <div className="hidden md:block mt-6 md:mt-0 md:w-[220px] lg:w-[260px] md:flex-shrink-0">
                    {/* Client lens drops the white card — the contact links read
                        cleaner flush on the page; questions card keeps its card. */}
                    <div className={`rounded-[2.5rem] sticky top-24 space-y-4 ${clientLens ? "" : "p-5 bg-white dark:bg-card"}`}>
                      {clientLens ? (
                        <ShopClientBooking shop={shop} hasInAppBooking={hasInAppBooking} />
                      ) : (
                      <>
                      <div>
                        <h3 className="text-sm font-medium mb-3 text-foreground">{t("detail.questions.title")}</h3>
                        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{t("detail.questions.subtitle")}</p>
                        <button
                          onClick={handleContactOwner}
                          className={`group inline-flex items-center gap-2 h-9 ${shop.logo_url ? "pl-1 pr-2.5" : "px-4"} rounded-full text-xs font-medium tracking-wide shadow-none hover:shadow-md active:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200`}
                          style={{ background: "#FAFAF8", color: "#0A0A0A" }}
                        >
                          {shop.logo_url && (
                            <span className="w-7 h-7 rounded-full overflow-hidden bg-white flex-shrink-0 ring-1 ring-black/10 group-hover:ring-black/20 group-hover:scale-105 transition-all duration-200">
                              <Image src={shop.logo_url} alt={shop.name} width={28} height={28} className="w-full h-full object-cover" />
                            </span>
                          )}
                          <span>{t("detail.questions.contact", { name: shop.contact_label || t("detail.questions.ownerFallback") })}</span>
                          <ArrowRight className="w-3.5 h-3.5 -ml-1 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
                        </button>
                      </div>
                      {shop.booking_url && (() => {
                        const platform = SHOP_BOOKING_LOGOS[shop.booking_platform || ""]
                          ? (shop.booking_platform as string)
                          : detectShopBookingPlatform(shop.booking_url)
                        const logo = SHOP_BOOKING_LOGOS[platform]
                        return (
                          <div className="pt-4 border-t border-border">
                            <p className="text-sm font-medium text-foreground mb-3">{t("detail.booking.heading")}</p>
                            <a
                              href={shop.booking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={SHOP_BOOKING_LABELS[platform] || t("detail.booking.linkAria")}
                              className="group inline-flex items-center gap-2.5 hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                            >
                              {logo ? (
                                /* Circular branded badge — all booking logos are square
                                    (1027×1027) so they sit cleanly in a circle. */
                                <span className="relative flex items-center justify-center w-[50px] h-[50px] rounded-full bg-white ring-1 ring-foreground/10 shadow-sm overflow-hidden group-hover:ring-foreground/25 group-hover:shadow-md transition-all duration-200">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={logo}
                                    alt={SHOP_BOOKING_LABELS[platform] || "Booking"}
                                    className="w-full h-full object-cover"
                                  />
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-neutral-900 text-white text-xs font-medium ring-1 ring-white/15 group-hover:bg-neutral-800 transition-colors duration-200">
                                  {t("detail.booking.online")}
                                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
                                </span>
                              )}
                            </a>
                          </div>
                        )
                      })()}
                      </>
                      )}
                    </div>
                  </div>
                </div>

              {!clientLens && (
              <>
              {/* 3. Amenities — full width row, multi-col internal grid so a
                   long list (16+ items) doesn't tower vertically. Left-aligned. */}
              {shop.amenities && shop.amenities.length > 0 && (
                <div className="pb-8 border-b border-gray-200 dark:border-border">
                  <div className="space-y-3">
                    <h2 className="text-sm font-medium text-foreground">{t("detail.amenitiesTitle")}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {shop.amenities.map((amenity, idx) => {
                        const config = getAmenityById(amenity)
                        const Icon = config?.icon
                        const label = amenityLabels[amenity] || config?.label || amenity
                        return (
                          <div key={idx} className="flex items-center gap-2.5">
                            {Icon ? (
                              <Icon className="w-5 h-5 text-foreground flex-shrink-0" strokeWidth={1.5} />
                            ) : (
                              <Check className="w-5 h-5 text-foreground flex-shrink-0" strokeWidth={1.5} />
                            )}
                            <span className="text-sm text-foreground">{label}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}
              </>
              )}

              {/* 4. Contact Shop - Mobile only (above location). For clients this
                  becomes the booking card (Call/Text/IG + Book an appointment). */}
              <div className="md:hidden">
                <div className={`rounded-[2.5rem] ${clientLens ? "" : "p-5 bg-white dark:bg-card"}`}>
                  {clientLens ? (
                    <ShopClientBooking shop={shop} hasInAppBooking={hasInAppBooking} />
                  ) : (
                  <>
                  <h3 className="text-sm font-medium mb-3 text-foreground">{t("detail.questions.title")}</h3>
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{t("detail.questions.subtitle")}</p>
                  <button
                    onClick={handleContactOwner}
                    className={`group inline-flex items-center gap-2 h-9 ${shop.logo_url ? "pl-1 pr-2.5" : "px-4"} rounded-full text-xs font-medium tracking-wide shadow-none hover:shadow-md active:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200`}
                          style={{ background: "#FAFAF8", color: "#0A0A0A" }}
                  >
                    {shop.logo_url && (
                      <span className="w-7 h-7 rounded-full overflow-hidden bg-white flex-shrink-0 ring-1 ring-black/10 group-hover:ring-black/20 group-hover:scale-105 transition-all duration-200">
                        <Image src={shop.logo_url} alt={shop.name} width={28} height={28} className="w-full h-full object-cover" />
                      </span>
                    )}
                    <span>{t("detail.questions.contact", { name: shop.contact_label || t("detail.questions.ownerFallback") })}</span>
                    <ArrowRight className="w-3.5 h-3.5 -ml-1 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
                  </button>
                  {shop.booking_url && (() => {
                    const platform = SHOP_BOOKING_LOGOS[shop.booking_platform || ""]
                      ? (shop.booking_platform as string)
                      : detectShopBookingPlatform(shop.booking_url)
                    const logo = SHOP_BOOKING_LOGOS[platform]
                    return (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm font-medium text-foreground mb-3">{t("detail.booking.heading")}</p>
                        <a
                          href={shop.booking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={SHOP_BOOKING_LABELS[platform] || t("detail.booking.linkAria")}
                          className="group inline-flex items-center gap-2.5 hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                        >
                          {logo ? (
                            <>
                              {/* Circular branded badge — all booking logos are square
                                  (1027×1027) so they sit cleanly in a circle. */}
                              <span className="relative flex items-center justify-center w-[54px] h-[54px] rounded-full bg-white ring-1 ring-foreground/10 shadow-sm overflow-hidden group-hover:ring-foreground/25 group-hover:shadow-md transition-all duration-200">
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={logo}
                                  alt={SHOP_BOOKING_LABELS[platform] || "Booking"}
                                  className="w-full h-full object-cover"
                                />
                              </span>
                              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-neutral-900 text-white text-xs font-medium ring-1 ring-white/15 group-hover:bg-neutral-800 transition-colors duration-200">
                              {t("detail.booking.online")}
                              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" strokeWidth={2} />
                            </span>
                          )}
                        </a>
                      </div>
                    )
                  })()}
                  </>
                  )}
                </div>
              </div>

              {/* The shop's Instagram, above where it is. Every shop with a
                  profile link gets it; a post or reel link has no grid. */}
              {(() => {
                const handle = instagramHandle(normalizeInstagramUrl(shop.instagram_url))
                return handle ? <InstagramGrid handle={handle} title={t("detail.instagram.title")} /> : null
              })()}

              {/* Location section — heading + address above, full-width map
                  below. No more 2-col side-by-side; map gets the full row. */}
              <div className="space-y-3">
                <div>
                  <h2 className="text-sm font-medium mb-3 text-foreground">{t("detail.location.title")}</h2>
                  <p className="text-sm text-foreground">{shopAddress}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shopAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline text-xs inline-block mt-1.5"
                  >
                    {t("detail.location.viewInMaps")}
                  </a>
                </div>
                {/* The embed needs its own key. Without one Google returns a
                    grey panel reading "Google Maps Platform rejected your
                    request", which on a public listing looks like the shop is
                    broken. No key → no map; the link above still works, since
                    the /maps/search URL is unauthenticated. */}
                {mapsEmbedKey && (
                  <div className="w-full h-[24rem] lg:h-[28rem] rounded-[2.5rem] overflow-hidden">
                    <iframe
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/place?key=${mapsEmbedKey}&q=${encodeURIComponent(shopAddress)}`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReportDialog
        open={isReportOpen}
        onOpenChange={setIsReportOpen}
        reportedId={shop.owner_id}
        type="listing"
        subjectName={shop.name}
        context={{ shop_id: shop.id, shop_slug: shop.slug ?? null }}
      />

      <ContactOwnerModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        shopId={shop.id}
        shopName={shop.name}
        ownerId={shop.owner_id}
        shopLogoUrl={shop.logo_url}
      />
      <SignUpPromptModal
        open={isSignUpModalOpen}
        onOpenChange={setIsSignUpModalOpen}
        boothOption={
          selectedPlan
            ? {
                plan_type: selectedPlan.billing_period || selectedPlan.plan_type || "monthly",
                price: selectedPlan.price,
              }
            : undefined
        }
      />
      <SignUpPromptModal
        open={isSaveSignUpModalOpen}
        onOpenChange={setIsSaveSignUpModalOpen}
        title={t("signupPrompt.title")}
        description={t("signupPrompt.description")}
      />
    </div>
  )
}

export { ListingsPageClient }
export default ListingsPageClient
